'use client'

import { useState, useCallback, useEffect } from 'react'
import { BestOfWizard } from './best-of-wizard'
import { BestOfResults } from './best-of-results'
import { EVT, track } from '@/lib/analytics'

interface LocationOption {
  id: string
  name: string
  type: string
  hasChildren: boolean
}

interface BestOfWizardClientProps {
  parks: { id: string; name: string; type: string }[]
  lands: { id: string; name: string; type: string }[]
  areas: { id: string; name: string; type: string }[]
}

interface Filters {
  type?: string
  locationId?: string
}

export function BestOfWizardClient({ parks, lands, areas }: BestOfWizardClientProps) {
  const [filters, setFilters] = useState<Filters>({})
  const [results, setResults] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [hasSearched, setHasSearched] = useState(false)
  const [initialLocations, setInitialLocations] = useState<LocationOption[]>([])

  // Fetch initial locations on mount
  useEffect(() => {
    const fetchInitialLocations = async () => {
      try {
        const response = await fetch('/api/locations')
        const data = await response.json()
        setInitialLocations(data.locations || [])
      } catch (error) {
        console.error('Failed to fetch initial locations:', error)
      }
    }
    fetchInitialLocations()
  }, [])

  const handleFilterChange = useCallback(async (newFilters: Filters) => {
    setFilters(newFilters)

    // Only fetch results if a type is selected
    if (!newFilters.type) {
      setResults([])
      setHasSearched(false)
      return
    }

    setLoading(true)
    setHasSearched(true)

    try {
      // Build query params
      const params = new URLSearchParams()
      if (newFilters.type) params.set('type', newFilters.type)
      if (newFilters.locationId) params.set('locationId', newFilters.locationId)

      const response = await fetch(`/api/best?${params.toString()}`)
      const data = await response.json()
      const entities = data.entities || []
      setResults(entities)
      track(EVT.BEST_OF_FILTER, {
        entity_type: newFilters.type,
        location_id: newFilters.locationId,
        result_count: entities.length,
      })
    } catch (error) {
      console.error('Failed to fetch results:', error)
      setResults([])
    } finally {
      setLoading(false)
    }
  }, [])

  const getFilterDescription = () => {
    const parts: string[] = []

    const typeNames: Record<string, string> = {
      ATTRACTION: 'Attractions',
      RESTAURANT: 'Restaurants',
      SHOW: 'Shows',
      ENTERTAINMENT: 'Entertainment',
      RESORT: 'Resorts',
    }

    if (filters.type) parts.push(typeNames[filters.type] || filters.type)

    return parts.length > 0 ? parts.join(' ') : 'Walt Disney World'
  }

  return (
    <div>
      <BestOfWizard
        initialLocations={initialLocations}
        onFilterChange={handleFilterChange}
      />

      {/* Results Section */}
      {hasSearched && (
        <div className="mt-8">
          <h2 className="text-2xl font-bold text-slate-800 mb-6">
            Best {getFilterDescription()}
          </h2>
          
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-[#1E4289] border-t-transparent"></div>
              <p className="mt-4 text-slate-600">Loading results...</p>
            </div>
          ) : (
            <BestOfResults results={results} />
          )}
        </div>
      )}

      {/* Initial state - no selection yet */}
      {!hasSearched && (
        <div className="text-center py-12 bg-white border border-slate-200 rounded-lg">
          <p className="text-slate-600 text-lg">
            Select a category above to see the best-rated experiences
          </p>
        </div>
      )}
    </div>
  )
}
